import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ChevronDown, ChevronRight, Download, FileText, Filter, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken, formatApiError } from "@/lib/api";
import AppShell from "@/components/app/AppShell";
import AdminTabs from "@/components/app/AdminTabs";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/useMediaQuery";

const STATUS_CHOICES = [
    { key: "", label: "All" },
    { key: "draft", label: "Draft" },
    { key: "certified", label: "Certified" },
];
const GROUP_CHOICES = [
    { key: "none", label: "None" },
    { key: "user", label: "User" },
    { key: "location", label: "Location" },
    { key: "job", label: "Job" },
];

const relTime = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
        return d.toLocaleDateString();
    } catch { return iso; }
};

const dayFromDate = (d) => {
    if (!d) return "";
    try {
        return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" });
    } catch { return ""; }
};

const SubmitterCell = ({ owner }) => (
    <div className="flex flex-col min-w-0">
        <span className="text-[14px] text-slate-900 truncate">
            {owner?.full_name || <span className="italic text-slate-400">Unnamed</span>}
        </span>
        <span className="text-[12px] text-slate-500 truncate">{owner?.email || "—"}</span>
    </div>
);

const HoursChip = ({ reg, ot, total }) => (
    <div className="flex flex-col items-end gap-0.5">
        <div className="text-[14px] font-medium tabular-nums text-slate-900">
            {total != null ? `${total} h` : "—"}
        </div>
        <div className="text-[11px] tabular-nums text-slate-500 flex gap-1">
            <span>R {reg != null ? reg : "0"}</span>
            <span className="text-slate-300">·</span>
            <span>OT {ot != null ? ot : "0"}</span>
        </div>
    </div>
);

const StatusPill = ({ status }) => (
    <span className={`chip ${status === "certified" ? "chip-certified" : "chip-draft"}`}>
        {status}
    </span>
);

const emptyFilters = () => ({
    q: "",
    user_id: "",
    status: "",
    date_from: "",
    date_to: "",
    group_by: "none",
});

const buildQuery = (f, extra = {}) => {
    const p = new URLSearchParams();
    // Broad `q` is client-side only; the API accepts specific filters.
    if (f.user_id) p.set("user_id", f.user_id);
    if (f.status) p.set("status", f.status);
    if (f.date_from) p.set("date_from", f.date_from);
    if (f.date_to) p.set("date_to", f.date_to);
    if (f.group_by && f.group_by !== "none") p.set("group_by", f.group_by);
    Object.entries(extra).forEach(([k, v]) => v && p.set(k, v));
    return p;
};

const AdminReportsPage = () => {
    const { user } = useAuth();
    const nav = useNavigate();
    const isMobile = useIsMobile();

    const [filters, setFilters] = useState(emptyFilters());
    const [data, setData] = useState(null);   // list endpoint response
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expandedGroups, setExpandedGroups] = useState({}); // {key: bool}
    const [sheetOpen, setSheetOpen] = useState(false);
    const [exportingCsv, setExportingCsv] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const p = buildQuery(filters, { page, limit: 50 });
            const { data } = await api.get(`/admin/reports?${p.toString()}`);
            setData(data);
        } catch (err) {
            if (err?.response?.status === 403) {
                nav("/reports", { replace: true });
                return;
            }
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [filters, page, nav]);

    // Non-admin guard
    useEffect(() => {
        if (user && user !== false && user.role !== "admin") {
            nav("/reports", { replace: true });
        }
    }, [user, nav]);

    // Load users for the dropdown once
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/reports/users-list");
                setUsers(data);
            } catch { /* non-blocking */ }
        })();
    }, []);

    useEffect(() => { load(); }, [load]);

    // Reset page when filters change (not on paging itself)
    useEffect(() => { setPage(1); }, [
        filters.user_id, filters.status, filters.date_from, filters.date_to,
        filters.group_by, filters.q,
    ]);

    const clearFilters = () => setFilters(emptyFilters());

    const doExport = async (format) => {
        const setter = format === "csv" ? setExportingCsv : setExportingPdf;
        setter(true);
        try {
            const p = buildQuery(filters, { format });
            const url = `${API}/admin/reports/export?${p.toString()}`;
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const cd = res.headers.get("Content-Disposition") || "";
            const m = /filename="?([^";]+)"?/.exec(cd);
            const filename = m ? m[1] : `impress-reports.${format}`;
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objUrl);
        } catch (err) {
            toast.error(`Export failed: ${err.message}`, { duration: 2500 });
        } finally {
            setter(false);
        }
    };

    // Client-side substring search across `q`
    const filteredRows = useMemo(() => {
        const rows = data?.rows || [];
        const q = filters.q.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const bag = [
                r.job_number, r.location,
                r.owner?.full_name, r.owner?.email,
            ].filter(Boolean).join(" ").toLowerCase();
            return bag.includes(q);
        });
    }, [data, filters.q]);

    const filteredGroups = useMemo(() => {
        const groups = data?.groups || [];
        const q = filters.q.trim().toLowerCase();
        if (!q) return groups;
        return groups
            .map((g) => ({
                ...g,
                rows: g.rows.filter((r) => {
                    const bag = [
                        r.job_number, r.location,
                        r.owner?.full_name, r.owner?.email,
                    ].filter(Boolean).join(" ").toLowerCase();
                    return bag.includes(q);
                }),
            }))
            .filter((g) => g.rows.length > 0);
    }, [data, filters.q]);

    const totalPages = data ? Math.max(1, Math.ceil((data.total || 0) / (data.limit || 50))) : 1;
    const grouped = filters.group_by !== "none";

    const renderRow = (r, showSubmitter = true) => (
        <tr key={r.report_id} data-testid={`admin-report-row-${r.report_id}`}>
            {showSubmitter && (
                <td>
                    <SubmitterCell owner={r.owner} />
                </td>
            )}
            <td>
                <span className="text-slate-900 font-medium">
                    {r.location || <span className="text-slate-400 italic">—</span>}
                </span>
            </td>
            <td className="tabular-nums">
                {r.job_number || <span className="text-slate-400">—</span>}
            </td>
            <td className="text-[13px]">
                <div className="tabular-nums text-slate-900">{r.report_date || "—"}</div>
                <div className="text-slate-500 text-[12px]">{dayFromDate(r.report_date)}</div>
            </td>
            <td className="text-right"><HoursChip reg={r.crew_regular_hours} ot={r.crew_overtime_hours} total={r.crew_total_hours} /></td>
            <td><StatusPill status={r.status} /></td>
            <td className="text-slate-500 text-[13px]">{relTime(r.updated_at)}</td>
            <td>
                <div className="flex gap-2 justify-end">
                    <Link
                        to={`/reports/${r.report_id}`}
                        className="btn btn-secondary btn-sm"
                        data-testid={`admin-report-view-${r.report_id}`}
                    >
                        View
                    </Link>
                    {r.status === "certified" && r.pdf_url && (
                        <a
                            href={r.pdf_url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-primary btn-sm btn-icon"
                            title="Download signed PDF"
                            aria-label="Download PDF"
                            data-testid={`admin-report-download-${r.report_id}`}
                        >
                            <Download size={13} strokeWidth={2} />
                        </a>
                    )}
                </div>
            </td>
        </tr>
    );

    const renderMobileCard = (r, showSubmitter = true) => (
        <div key={r.report_id} className="glass-card !py-4" data-testid={`admin-report-card-${r.report_id}`}>
            {showSubmitter && (
                <div className="pb-2 mb-2 border-b border-[var(--divider)]">
                    <SubmitterCell owner={r.owner} />
                </div>
            )}
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-medium text-slate-900 truncate">
                        {r.location || <span className="text-slate-400 italic">No location</span>}
                    </div>
                    <div className="text-[12px] text-slate-500 mt-1">
                        Job {r.job_number || "—"} · {r.report_date || "—"} {dayFromDate(r.report_date) && `· ${dayFromDate(r.report_date)}`}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                        <StatusPill status={r.status} />
                        <span className="text-[12px] text-slate-500">{relTime(r.updated_at)}</span>
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <HoursChip reg={r.crew_regular_hours} ot={r.crew_overtime_hours} total={r.crew_total_hours} />
                </div>
            </div>
            <div className="mt-3 flex gap-2">
                <Link to={`/reports/${r.report_id}`} className="btn btn-secondary btn-sm">
                    View
                </Link>
                {r.status === "certified" && r.pdf_url && (
                    <a href={r.pdf_url} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm">
                        <Download size={13} strokeWidth={2} />
                        PDF
                    </a>
                )}
            </div>
        </div>
    );

    const activeFilterCount = (
        (filters.user_id ? 1 : 0) + (filters.status ? 1 : 0) +
        (filters.date_from ? 1 : 0) + (filters.date_to ? 1 : 0) +
        (filters.group_by !== "none" ? 1 : 0) + (filters.q ? 1 : 0)
    );

    const FiltersContent = () => (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="label" htmlFor="ar-q">Search</label>
                    <input
                        id="ar-q"
                        className="input"
                        placeholder="Job #, location, name, email…"
                        value={filters.q}
                        onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                        data-testid="filter-q"
                    />
                </div>
                <div>
                    <label className="label" htmlFor="ar-user">User</label>
                    <select
                        id="ar-user"
                        className="select"
                        value={filters.user_id}
                        onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}
                        data-testid="filter-user"
                    >
                        <option value="">Any user</option>
                        {users.map((u) => (
                            <option key={u.id} value={u.id}>
                                {(u.full_name || u.email)}{u.full_name ? ` — ${u.email}` : ""}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="label" htmlFor="ar-from">From</label>
                    <input
                        id="ar-from"
                        type="date"
                        className="input"
                        value={filters.date_from}
                        onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                        data-testid="filter-date-from"
                    />
                </div>
                <div>
                    <label className="label" htmlFor="ar-to">To</label>
                    <input
                        id="ar-to"
                        type="date"
                        className="input"
                        value={filters.date_to}
                        onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                        data-testid="filter-date-to"
                    />
                </div>
            </div>
            <div>
                <div className="label mb-2">Status</div>
                <div className="tab-pill-group inline-flex" role="tablist" data-testid="filter-status">
                    {STATUS_CHOICES.map((s) => (
                        <button
                            key={s.key || "all"}
                            type="button"
                            className="tab-pill"
                            data-active={filters.status === s.key}
                            onClick={() => setFilters((f) => ({ ...f, status: s.key }))}
                            data-testid={`filter-status-${s.key || "all"}`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <div className="label mb-2">Group by</div>
                <div className="tab-pill-group inline-flex" role="tablist" data-testid="filter-group-by">
                    {GROUP_CHOICES.map((g) => (
                        <button
                            key={g.key}
                            type="button"
                            className="tab-pill"
                            data-active={filters.group_by === g.key}
                            onClick={() => setFilters((f) => ({ ...f, group_by: g.key }))}
                            data-testid={`filter-group-${g.key}`}
                        >
                            {g.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="flex items-center gap-2 pt-2 flex-wrap">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => doExport("csv")}
                    disabled={exportingCsv}
                    data-testid="export-csv-btn"
                >
                    <FileText size={14} strokeWidth={2} />
                    {exportingCsv ? "Exporting…" : "Export CSV"}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => doExport("pdf")}
                    disabled={exportingPdf}
                    data-testid="export-pdf-btn"
                >
                    <Download size={14} strokeWidth={2} />
                    {exportingPdf ? "Exporting…" : "Export PDF"}
                </button>
                {activeFilterCount > 0 && (
                    <button
                        type="button"
                        className="text-[13px] text-slate-500 ml-auto hover:text-slate-800"
                        onClick={clearFilters}
                        data-testid="filter-clear"
                    >
                        Clear filters
                    </button>
                )}
            </div>
        </div>
    );

    const TableHeader = () => (
        <thead>
            <tr>
                {!grouped && <th style={{ width: "18%" }}>Submitter</th>}
                <th style={{ width: grouped ? "26%" : "20%" }}>Location</th>
                <th style={{ width: "10%" }}>Job #</th>
                <th style={{ width: "12%" }}>Date · Day</th>
                <th style={{ width: "14%" }} className="text-right">Crew hrs</th>
                <th style={{ width: "10%" }}>Status</th>
                <th style={{ width: "10%" }}>Updated</th>
                <th style={{ width: "6%" }} className="text-right">Actions</th>
            </tr>
        </thead>
    );

    return (
        <AppShell>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                <div>
                    <div className="text-[13px] uppercase tracking-wider text-slate-400">Admin</div>
                    <h1 className="text-3xl font-semibold text-slate-900 mt-1" data-testid="admin-reports-title">
                        Reports
                    </h1>
                </div>
                {data && (
                    <div className="text-[13px] text-slate-500">
                        {data.total} report{data.total === 1 ? "" : "s"} total
                    </div>
                )}
            </div>

            <AdminTabs />

            {/* Mobile filter pill */}
            <div className="mobile-only mb-4">
                <button
                    type="button"
                    className="btn btn-secondary w-full"
                    onClick={() => setSheetOpen(true)}
                    data-testid="admin-reports-filters-pill"
                >
                    <Filter size={14} strokeWidth={2} />
                    Filters
                    {activeFilterCount > 0 && (
                        <span className="chip chip-certified ml-1">{activeFilterCount}</span>
                    )}
                </button>
            </div>

            {/* Desktop toolbar */}
            <div className="glass-card mb-5 desktop-only" data-testid="admin-reports-toolbar">
                <FiltersContent />
            </div>

            {error && (
                <div className="err rounded-[10px] border border-red-200 bg-red-50/70 px-3 py-2 mb-4" data-testid="admin-reports-error">
                    {error}
                </div>
            )}

            {loading && !data ? (
                <div className="glass-card text-[13px] text-slate-500">Loading…</div>
            ) : (data.total || 0) === 0 || (grouped ? filteredGroups.length === 0 : filteredRows.length === 0) ? (
                <div className="glass-card text-center py-10" data-testid="admin-reports-empty">
                    <div className="text-[14px] text-slate-500 mb-3">
                        No reports match your filters.
                    </div>
                    {activeFilterCount > 0 && (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={clearFilters}
                            data-testid="admin-reports-clear-empty"
                        >
                            Clear filters
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {/* GROUPED */}
                    {grouped ? (
                        <div className="flex flex-col gap-3">
                            {filteredGroups.map((g) => {
                                const open = expandedGroups[g.key] !== false;
                                return (
                                    <div key={g.key} className="glass-card !p-0 overflow-hidden" data-testid={`admin-group-${g.key}`}>
                                        <button
                                            type="button"
                                            onClick={() => setExpandedGroups((s) => ({ ...s, [g.key]: !open }))}
                                            className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50"
                                            data-testid={`admin-group-toggle-${g.key}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                <span className="text-[15px] font-semibold text-slate-900">{g.label}</span>
                                                <span className="chip chip-neutral">{g.count} report{g.count === 1 ? "" : "s"}</span>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-[11px] uppercase tracking-wider text-slate-400">Sum crew hrs</div>
                                                <div className="text-[15px] font-medium tabular-nums text-slate-900">{g.sum_crew_hours} h</div>
                                            </div>
                                        </button>
                                        {open && (
                                            <>
                                                <div className="desktop-only border-t border-[var(--divider)] overflow-x-auto">
                                                    <table className="g-table" data-testid={`admin-group-table-${g.key}`}>
                                                        <TableHeader />
                                                        <tbody>
                                                            {g.rows.map((r) => renderRow(r, filters.group_by !== "user"))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <div className="mobile-only border-t border-[var(--divider)] p-3 flex flex-col gap-3">
                                                    {g.rows.map((r) => renderMobileCard(r, filters.group_by !== "user"))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        /* FLAT */
                        <>
                            <div className="glass-card !p-0 overflow-hidden desktop-only">
                                <div className="overflow-x-auto">
                                    <table className="g-table" data-testid="admin-reports-table">
                                        <TableHeader />
                                        <tbody>
                                            {filteredRows.map((r) => renderRow(r, true))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="mobile-only flex flex-col gap-3" data-testid="admin-reports-mobile-list">
                                {filteredRows.map((r) => renderMobileCard(r, true))}
                            </div>
                        </>
                    )}

                    {/* Pagination */}
                    {!grouped && data.total > data.limit && (
                        <div className="flex items-center justify-center gap-3 mt-6" data-testid="admin-reports-pagination">
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                data-testid="pagination-prev"
                            >
                                Prev
                            </button>
                            <span className="text-[13px] text-slate-600">
                                Page <span className="tabular-nums font-medium">{page}</span> of <span className="tabular-nums">{totalPages}</span>
                            </span>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                data-testid="pagination-next"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Mobile filters sheet */}
            {isMobile && sheetOpen && (
                <>
                    <div className="sheet-backdrop" onClick={() => setSheetOpen(false)} aria-hidden />
                    <div className="sheet" role="dialog" aria-modal="true" data-testid="admin-reports-filters-sheet">
                        <div className="sheet-header">
                            <button
                                type="button"
                                onClick={() => setSheetOpen(false)}
                                className="btn btn-secondary btn-sm btn-icon"
                                aria-label="Close"
                                data-testid="admin-reports-filters-close"
                            >
                                <X size={16} strokeWidth={2} />
                            </button>
                            <h2>Filters</h2>
                        </div>
                        <div className="sheet-body">
                            <FiltersContent />
                        </div>
                        <div className="sheet-footer">
                            <button
                                type="button"
                                className="btn btn-primary w-full"
                                onClick={() => setSheetOpen(false)}
                                data-testid="admin-reports-filters-apply"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </>
            )}
        </AppShell>
    );
};

export default AdminReportsPage;
